import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { adminApiService } from '../../services/adminApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import type { LogEntry } from '../../types'
import AdminTabs from './AdminTabs'
import LogCleanupPanel from './LogCleanupPanel'

const LEVELS = ['', 'Information', 'Warning', 'Error', 'Fatal']
const PAGE_SIZE = 100

/** Level → colour. Anything unrecognised falls back to plain text rather than going unstyled. */
function levelColor(level: string | null): string {
  switch (level) {
    case 'Fatal':
    case 'Error': return colors.error
    case 'Warning': return colors.warning
    default: return colors.textSecondary
  }
}

/**
 * One row as plain text for pasting into a bug report or a chat. Tab-separated header line plus
 * the exception on its own lines, because an exception pasted inline is unreadable and the stack
 * trace is the part worth sharing.
 */
function rowAsText(row: LogEntry): string {
  const head = [
    row.timeStamp ?? '',
    row.level ?? '',
    row.httpStatus != null ? String(row.httpStatus) : '',
    row.httpMethod && row.url ? `${row.httpMethod} ${row.url}` : '',
    row.message ?? '',
  ].filter(Boolean).join('\t')
  return row.exception ? `${head}\n${row.exception}` : head
}

function formatTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().replace('T', ' ').slice(0, 23)
}

export default function AdminLogsPage() {
  const [rows, setRows] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  async function copyRow(row: LogEntry) {
    try {
      await navigator.clipboard.writeText(rowAsText(row))
      setCopied(row.logEntryId)
      window.setTimeout(() => setCopied(c => (c === row.logEntryId ? null : c)), 1500)
    } catch {
      // Clipboard access is denied outside a secure context and in some embedded browsers.
      // Failing silently would look like the button does nothing, so say so.
      setError('Could not copy to the clipboard — the browser blocked it.')
    }
  }

  // Source and job filters live in the URL so the Jobs tab can link to one job's (or one run's) lines.
  const [params, setParams] = useSearchParams()
  const source = (params.get('source') ?? (params.get('job') ? 'jobs' : '')) as '' | 'requests' | 'jobs'
  const jobName = params.get('job') ?? ''
  const jobRunId = params.get('run') ?? ''
  function setParam(updates: Record<string, string>) {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(updates)) { if (v) next.set(k, v); else next.delete(k) }
    setPage(1)
    setParams(next, { replace: true })
  }

  const [minimumLevel, setMinimumLevel] = useState('')
  const [search, setSearch] = useState('')
  const [failuresOnly, setFailuresOnly] = useState(false)
  const [page, setPage] = useState(1)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setRows(await adminApiService.getLogs({
        page,
        pageSize: PAGE_SIZE,
        minimumLevel: minimumLevel || null,
        search: search.trim() || null,
        // 400 rather than 500: a rejected request is a failure worth seeing even when the server
        // handled it cleanly, and those are exactly the ones that show up as a client-side error.
        minimumHttpStatus: failuresOnly ? 400 : null,
        source: source || null,
        jobName: jobName || null,
        jobRunId: jobRunId || null,
      }))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load logs')
    } finally {
      setLoading(false)
    }
  }, [page, minimumLevel, search, failuresOnly, source, jobName, jobRunId])

  useEffect(() => { load() }, [load])

  const inputStyle = {
    padding: '5px 8px', border: `1px solid ${colors.border}`,
    borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none',
  } as const

  return (
    <div>
      <AdminTabs />

      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, marginBottom: spacing.md }}>
        Logs
      </h1>

      <LogCleanupPanel onPurged={load} />

      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap', marginBottom: spacing.md }}>
        <label style={{ fontSize: fontSize.sm, color: colors.textSecondary, display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
          Level
          <select
            aria-label="Minimum level"
            value={minimumLevel}
            onChange={e => { setPage(1); setMinimumLevel(e.target.value) }}
            style={inputStyle}
          >
            {LEVELS.map(l => <option key={l} value={l}>{l || 'All'}</option>)}
          </select>
        </label>

        <label style={{ fontSize: fontSize.sm, color: colors.textSecondary, display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
          Source
          <select
            aria-label="Source"
            value={source}
            onChange={e => setParam({ source: e.target.value, job: '', run: '' })}
            style={inputStyle}
          >
            <option value="">All</option>
            <option value="requests">Requests</option>
            <option value="jobs">Background jobs</option>
          </select>
        </label>

        <input
          type="search"
          aria-label="Search logs"
          placeholder="Search message, URL or exception"
          value={search}
          onChange={e => { setPage(1); setSearch(e.target.value) }}
          style={{ ...inputStyle, minWidth: 280 }}
        />

        <label style={{ fontSize: fontSize.sm, color: colors.textSecondary, display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={failuresOnly}
            onChange={e => { setPage(1); setFailuresOnly(e.target.checked) }}
          />
          Failed requests only
        </label>

        <button type="button" onClick={load} style={{ ...inputStyle, cursor: 'pointer', background: colors.surface }}>
          Refresh
        </button>
      </div>

      {jobName && (
        <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center', flexWrap: 'wrap', marginBottom: spacing.md, fontSize: fontSize.sm }}>
          <span style={{ color: colors.textSecondary }}>Showing</span>
          <span style={{ padding: '2px 10px', borderRadius: 999, background: colors.infoLight, color: colors.info, fontWeight: 600 }}>
            {jobName}{jobRunId ? ` · run ${jobRunId.slice(0, 8)}` : ' · all runs'}
          </span>
          {jobRunId && (
            <button type="button" onClick={() => setParam({ run: '' })} style={{ ...inputStyle, cursor: 'pointer', background: colors.surface }}>All runs of this job</button>
          )}
          <button type="button" aria-label="Clear job filter" onClick={() => setParam({ job: '', run: '' })} style={{ ...inputStyle, cursor: 'pointer', background: colors.surface }}>Clear</button>
        </div>
      )}

      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: colors.textSecondary }}>No log entries match these filters.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.xs, fontFamily: 'monospace' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: colors.textSecondary }}>
              <th style={{ padding: spacing.xs, whiteSpace: 'nowrap' }}>Time</th>
              <th style={{ padding: spacing.xs }}>Level</th>
              <th style={{ padding: spacing.xs }}>Status</th>
              <th style={{ padding: spacing.xs }}>Source</th>
              <th style={{ padding: spacing.xs, width: '100%' }}>Message</th>
              <th style={{ padding: spacing.xs }}><span style={{ position: 'absolute', left: -9999 }}>Copy</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr
                key={row.logEntryId}
                onClick={() => setExpanded(expanded === row.logEntryId ? null : row.logEntryId)}
                style={{ borderTop: `1px solid ${colors.border}`, cursor: row.exception ? 'pointer' : 'default', verticalAlign: 'top' }}
              >
                <td style={{ padding: spacing.xs, whiteSpace: 'nowrap', color: colors.textSecondary }}>{formatTime(row.timeStamp)}</td>
                <td style={{ padding: spacing.xs, color: levelColor(row.level), fontWeight: 600 }}>{row.level ?? '—'}</td>
                <td style={{ padding: spacing.xs, color: (row.httpStatus ?? 0) >= 400 ? colors.error : colors.textSecondary }}>
                  {row.httpStatus ?? ''}
                </td>
                <td style={{ padding: spacing.xs, whiteSpace: 'nowrap' }}>
                  {row.httpMethod ? `${row.httpMethod} ${row.url ?? ''}` : row.jobName ? (
                    <button type="button" title="Show this run's lines"
                      onClick={e => { e.stopPropagation(); setParam({ source: 'jobs', job: row.jobName ?? '', run: row.jobRunId ?? '' }) }}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: colors.info, font: 'inherit', textDecoration: 'underline' }}>
                      {row.jobName}
                    </button>
                  ) : ''}
                </td>
                <td style={{ padding: spacing.xs, wordBreak: 'break-word' }}>
                  {row.message}
                  {row.exception && expanded === row.logEntryId && (
                    <pre style={{
                      marginTop: spacing.xs, padding: spacing.sm, background: colors.errorLight,
                      color: colors.error, borderRadius: radius.sm, whiteSpace: 'pre-wrap', overflowX: 'auto',
                    }}>{row.exception}</pre>
                  )}
                  {row.exception && expanded !== row.logEntryId && (
                    <span style={{ color: colors.error, marginLeft: spacing.xs }}>▸ exception</span>
                  )}
                </td>
                <td style={{ padding: spacing.xs, whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    aria-label="Copy log entry"
                    title="Copy this entry, including the exception"
                    // The row toggles the exception open on click; without this the copy button
                    // would expand it too.
                    onClick={e => { e.stopPropagation(); copyRow(row) }}
                    style={{
                      border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                      background: colors.surface, cursor: 'pointer', padding: '2px 6px',
                      fontSize: fontSize.xs, color: copied === row.logEntryId ? colors.success : colors.textSecondary,
                      lineHeight: 1.4,
                    }}
                  >
                    {copied === row.logEntryId ? '✓' : '⧉'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', marginTop: spacing.md }}>
        <button
          type="button"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          style={{ ...inputStyle, cursor: page === 1 ? 'default' : 'pointer', background: colors.surface }}
        >
          Previous
        </button>
        <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>Page {page}</span>
        <button
          type="button"
          onClick={() => setPage(p => p + 1)}
          // A full page implies there may be another; a short one is the end. The endpoint returns
          // a plain list, so there is no total to page against without a second count query.
          disabled={rows.length < PAGE_SIZE}
          style={{ ...inputStyle, cursor: rows.length < PAGE_SIZE ? 'default' : 'pointer', background: colors.surface }}
        >
          Next
        </button>
      </div>
    </div>
  )
}
